import { useState } from 'react'
import { flushSync } from 'react-dom'
import type { DashboardSection } from '@/features/dashboard/api/schemas'
import { MarketFilters } from '@/features/dashboard/filters/SectionFilters'
import { useSectionSelection } from '@/features/dashboard/filters/useDashboardFilters'
import { sectionSubtitle } from '@/features/dashboard/model/sections'
import {
  homeCardAnchor,
  recallExpanded,
  rememberExpanded,
  scrollCardIntoView,
} from '@/features/dashboard/model/homeReturn'
import { cx } from '@/shared/lib/classNames'
import { tileButtonId, withViewTransition } from '@/shared/lib/viewTransition'
import { ExpandedModule } from './ExpandedModule'
import { ModulePill } from './ModulePill'
import { ModuleTile } from './ModuleTile'
import { SectionIcon } from './SectionIcon'
import styles from './SectionGroup.module.css'

type Expanded = { cardId: number; fromKeyboard: boolean }

type SectionGroupProps = {
  section: DashboardSection
}

/**
 * A section: its icon and title, then its tiles in a grid. Clicking one expands it into a panel beside them, narrowing the tiles
 * to a one-tile-wide sidebar where the expanded card's tile becomes a pill;
 * clicking another tile switches the expanded card and the panel's back arrow
 * returns to the grid. The section's market filter shows in its header row
 * while a card is expanded, and the timeframe in the panel (see
 * ExpandedModule). The change morphs through a view transition where the
 * browser supports one.
 */
export const SectionGroup = ({ section }: SectionGroupProps) => {
  // Returning from a deep dive reopens the card the user left expanded.
  const [expanded, setExpandedState] = useState<Expanded | null>(() =>
    recallExpanded(section.id)
  )
  const setExpanded = (next: Expanded | null) => {
    rememberExpanded(section.id, next)
    setExpandedState(next)
  }
  const expandedStub = section.cards.find(
    card => card.cardId === expanded?.cardId
  )
  const headingId = `section-${section.id}`
  const { selection } = useSectionSelection(section.id)
  const subtitle = sectionSubtitle(section, selection?.timeframe)

  // Once the panel has its final layout, the least scroll that shows all of it.
  const expand = (cardId: number, fromKeyboard: boolean) =>
    withViewTransition(
      () => flushSync(() => setExpanded({ cardId, fromKeyboard })),
      () => {
        const panel = document.getElementById(homeCardAnchor(cardId))
        if (panel) scrollCardIntoView(panel, { align: 'nearest', smooth: true })
      }
    )

  const collapse = (fromKeyboard: boolean) => {
    const cardId = expanded?.cardId
    withViewTransition(() => {
      flushSync(() => setExpanded(null))
      // Keyboard users go back to the tile they opened.
      if (fromKeyboard && cardId !== undefined) {
        document.getElementById(tileButtonId(cardId))?.focus()
      }
    })
  }

  return (
    <section
      className={cx(styles.group, expandedStub && styles.expanded)}
      aria-labelledby={headingId}
    >
      <header className={styles.header}>
        <div className={cx(styles.title, !subtitle && styles.single)}>
          <SectionIcon sectionId={section.id} className={styles.icon} />
          <div className={styles.text}>
            <h2 id={headingId} className={styles.heading}>
              {section.title}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
        {expandedStub && (
          <div className={styles.filters}>
            <MarketFilters section={section} />
          </div>
        )}
      </header>
      <div className={styles.body}>
        <div className={styles.list}>
          {section.cards.map(stub =>
            stub.cardId === expandedStub?.cardId ? (
              <ModulePill key={stub.cardId} name={stub.name} />
            ) : (
              <ModuleTile
                key={stub.cardId}
                stub={stub}
                sectionId={section.id}
                onExpand={fromKeyboard => expand(stub.cardId, fromKeyboard)}
              />
            )
          )}
        </div>
        {expandedStub && (
          <div className={styles.panel}>
            <ExpandedModule
              key={expandedStub.cardId}
              stub={expandedStub}
              section={section}
              takeFocus={expanded?.fromKeyboard ?? false}
              onClose={collapse}
            />
          </div>
        )}
      </div>
    </section>
  )
}
